import type { YMapControls, YMapControl, YMapGroupEntity, YMap, LngLat } from '@yandex/ymaps3-types'
import type { ClusterFeature } from '@/modules/YandexMap3Module/map-cluster.types'
import type { Ref } from 'vue'

import type {
    DispatcherWorkplaceInitializeHelperModuleOption,
    DispatcherWorkplaceDestroyHelperModuleOption,
    ClusterFeatureEntity,
    FeaturePopupsOpenPopupOptions,
    DispatcherWorkplaceDataEntity,
    DispatcherWorkplaceTake,
    DispatcherWorkplaceDelivery,
    DispatcherWorkplaceItem
} from '@/modules/DispatcherWorkplaceModule/types'

import { reactive, computed } from 'vue'

import DwDoneIcon from '@/modules/DispatcherWorkplaceModule/components/DwDoneIcon/index.vue'
import DwSidebarEntity from '@/modules/DispatcherWorkplaceModule/components/DwSidebar/Entity.vue'

import mapControls from '@/modules/YandexMap3Module/map-controls'
import mapCluster from '@/modules/YandexMap3Module/map-cluster'

import dwMapHelpers from '@/modules/DispatcherWorkplaceModule/dw-map-helpers'

import { useDebounce } from 'vue-composable'

const GRID_SIZE = 100
const DEBOUNCE_TIME = 1000

type QuickFiltersCallbacks = {
    onUndeliveredDeliveriesChanged: (is_enabled: boolean) => void
    onUnassignedTakesChanged: (is_enabled: boolean) => void
}

export default function({ openPopup, selectedFeatureId }: {
    openPopup: (options: FeaturePopupsOpenPopupOptions) => void
    selectedFeatureId: Ref<number | null>
}) {
    let mapInstance: YMap | null = null
    let ordersClusterer: any | null = null

    let ordersControlsGroup: YMapControls | null = null
    let ordersControlsControl: YMapControl | null = null
    let ordersControlsHorizontalGroup: YMapGroupEntity<{}> | null = null

    let quickFiltersCallbacks: QuickFiltersCallbacks | null = null

    const state = reactive({
        list: [] as DispatcherWorkplaceDataEntity[],
        quick_filters: {
            completed_orders: false,
            deliveries: true,
            takes: true,
            undelivered_deliveries: false,
            unassigned_takes: false,
            order_or_invoice_number: null as string | null,
            finded_orders_or_invoices: [] as DispatcherWorkplaceDataEntity[]
        }
    })

    const updateList = (orders: Array<DispatcherWorkplaceDataEntity>) => {
        state.list = orders
    }

    const onFindedEntityClicked = (entity: DispatcherWorkplaceDataEntity) => {
        dwMapHelpers.updateMapLocation({
            map: mapInstance as YMap,
            coordinates: [Number(entity.longitude as string), Number(entity.latitude as string)],
            zoom: 17
        })

        state.quick_filters.order_or_invoice_number = null
        state.quick_filters.finded_orders_or_invoices = []
    }

    const searchOrder = () => {
        if (!state.quick_filters.order_or_invoice_number) {
            state.quick_filters.finded_orders_or_invoices = []
            return
        }
        
        state.quick_filters.finded_orders_or_invoices = state.list.filter((entity) => {
            const query = (state.quick_filters.order_or_invoice_number as string).toLowerCase()

            if (entity.type === 1) {
                const entityOrderNumber = (entity as DispatcherWorkplaceTake).orderNumber.toLowerCase()
                return entityOrderNumber.includes(query)
            }

            if (entity.type === 2) {
                const entityInvoiceNumber = (entity as DispatcherWorkplaceDelivery).invoiceNumber.toLowerCase()
                return entityInvoiceNumber.includes(query)
            }
        })
    }

    const debouncedSearchOrder = useDebounce(searchOrder, DEBOUNCE_TIME)

    const initializeMapControls = () => {
        if (!ordersControlsGroup) return

        const { YMapControl } = ymaps3

        ordersControlsControl = new YMapControl()
        ordersControlsGroup.addChild(ordersControlsControl)

        ordersControlsHorizontalGroup = mapControls.createHorizontalControlsGroup()
        ordersControlsControl.addChild(ordersControlsHorizontalGroup)

        ordersControlsHorizontalGroup.addChild(
            mapControls.createIconButtonControl({
                checked: computed(() => state.quick_filters.completed_orders),
                icon: DwDoneIcon,
                onChange: () => {
                    state.quick_filters.completed_orders = !state.quick_filters.completed_orders

                    if (state.quick_filters.completed_orders) {
                        state.quick_filters.unassigned_takes = false
                        state.quick_filters.undelivered_deliveries = false

                        if (quickFiltersCallbacks && quickFiltersCallbacks.onUndeliveredDeliveriesChanged) {
                            quickFiltersCallbacks.onUndeliveredDeliveriesChanged(state.quick_filters.undelivered_deliveries)
                        }

                        if (quickFiltersCallbacks && quickFiltersCallbacks.onUnassignedTakesChanged) {
                            quickFiltersCallbacks.onUnassignedTakesChanged(state.quick_filters.unassigned_takes)
                        }

                        if (state.quick_filters.takes === false && state.quick_filters.deliveries === false) {
                            state.quick_filters.takes = true
                            state.quick_filters.deliveries = true
                        }
                    }

                    updateCluster()
                }
            })
        )

        ordersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.deliveries),
                label: 'Доставки',
                onChange: () => {
                    state.quick_filters.deliveries = !state.quick_filters.deliveries
                    updateCluster()
                }
            })
        )

        ordersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.takes),
                label: 'Заборы',
                onChange: () => {
                    state.quick_filters.takes = !state.quick_filters.takes
                    updateCluster()
                }
            })
        )

        ordersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.undelivered_deliveries),
                label: 'Невыданные доставки',
                onChange: () => {
                    state.quick_filters.undelivered_deliveries = !state.quick_filters.undelivered_deliveries

                    if (state.quick_filters.undelivered_deliveries) {
                        state.quick_filters.completed_orders = false
                    }

                    updateCluster()

                    if (quickFiltersCallbacks && quickFiltersCallbacks.onUndeliveredDeliveriesChanged) {
                        quickFiltersCallbacks.onUndeliveredDeliveriesChanged(state.quick_filters.undelivered_deliveries)
                    }
                }
            })
        )

        ordersControlsHorizontalGroup.addChild(
            mapControls.createCheckboxControl({
                checked: computed(() => state.quick_filters.unassigned_takes),
                label: 'Неназначенные заборы',
                onChange: () => {
                    state.quick_filters.unassigned_takes = !state.quick_filters.unassigned_takes

                    if (state.quick_filters.unassigned_takes) {
                        state.quick_filters.completed_orders = false
                    }

                    updateCluster()

                    if (quickFiltersCallbacks && quickFiltersCallbacks.onUnassignedTakesChanged) {
                        quickFiltersCallbacks.onUnassignedTakesChanged(state.quick_filters.unassigned_takes)
                    }
                }
            })
        )

        ordersControlsHorizontalGroup.addChild(
            mapControls.createSearchControl({
                value: computed(() => state.quick_filters.order_or_invoice_number),
                placeholder: 'Поиск по номеру заказа или накладной',
                options: computed(() => state.quick_filters.finded_orders_or_invoices),
                option: DwSidebarEntity,
                onInput: (event: Event) => {
                    const target = event.target as HTMLInputElement
                    state.quick_filters.order_or_invoice_number = target.value
                    debouncedSearchOrder()
                },
                onOptionClicked: onFindedEntityClicked
            })
        )
    }

    const destroyMapControls = () => {
        if (!ordersControlsGroup || !ordersControlsControl || !ordersControlsHorizontalGroup) return

        ordersControlsControl.removeChild(ordersControlsHorizontalGroup)
        ordersControlsHorizontalGroup = null

        ordersControlsGroup.removeChild(ordersControlsControl)
        ordersControlsControl = null
    }

    const isTakeCompleted = (entity: DispatcherWorkplaceItem) => {
        if (entity.type === 1) {
            return entity.state === 1 || entity.statusId === 3
        }

        return false
    }

    const isDeliveryCompleted = (entity: DispatcherWorkplaceItem) => {
        if (entity.type === 2) {
            return entity.state === 1
        }

        return false
    }

    const isFeatureEntityCompleted = (entity: DispatcherWorkplaceItem) => {
        if (entity.type === 1) {
            return isTakeCompleted(entity)
        }

        if (entity.type === 2) {
            return isDeliveryCompleted(entity)
        }

        return false
    }

    const getActualClusterFeatures = () => {
        let ordersToRender: DispatcherWorkplaceDataEntity[] = []

        const completedTakes = state.list.filter(isTakeCompleted)
        const completedDeliveries = state.list.filter(isDeliveryCompleted)

        const takes = state.list.filter((order) => order.type === 1 && !((order as DispatcherWorkplaceTake).notAssigned) && !isFeatureEntityCompleted(order))
        const deliveries = state.list.filter((order) => order.type === 2 && !((order as DispatcherWorkplaceDelivery).notDelivered) && !isFeatureEntityCompleted(order))

        const unassigned = state.list.filter((order) => order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned)
        const undelivered = state.list.filter((order) => order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered)

        ordersToRender = state.list.filter((order) => {
            const isCompletedTake = isTakeCompleted(order)
            const isCompletedDelivery = isDeliveryCompleted(order)

            const isTake = order.type === 1 && !(order as DispatcherWorkplaceTake).notAssigned && !isFeatureEntityCompleted(order)
            const isDelivery = order.type === 2 && !(order as DispatcherWorkplaceDelivery).notDelivered && !isFeatureEntityCompleted(order)
            const isUnassignedTake = order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned
            const isUndeliveredDelivery = order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered

            return !isCompletedTake && !isCompletedDelivery && !isTake && !isDelivery && !isUnassignedTake && !isUndeliveredDelivery
        })

        if (state.quick_filters.completed_orders) {
            if (state.quick_filters.takes) {
                ordersToRender = ordersToRender.concat(completedTakes)
            }

            if (state.quick_filters.deliveries) {
                ordersToRender = ordersToRender.concat(completedDeliveries)
            }
        } else {
            if (state.quick_filters.takes) {
                ordersToRender = ordersToRender.concat(takes)
            }

            if (state.quick_filters.deliveries) {
                ordersToRender = ordersToRender.concat(deliveries)
            }
        }

        if (state.quick_filters.unassigned_takes) {
            ordersToRender = ordersToRender.concat(unassigned)
        }

        if (state.quick_filters.undelivered_deliveries) {
            ordersToRender = ordersToRender.concat(undelivered)
        }

        return ordersToRender
    }

    const updateCluster = () => {
        if (!ordersClusterer) return

        const features = getActualClusterFeatures().map(dwMapHelpers.convertOrderToClusterFeature)
        ordersClusterer.update({ features })
    }

    const onFeatureChangeDate = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: true,
            is_add_to_wait_list: false,
            is_assign: false,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureAddToWaitList = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: true,
            is_assign: false,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureAssign = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: false,
            is_assign: true,
            is_self_delivery: false,
            feature
        })
    }

    const onFeatureSelfDelivery = (feature: ClusterFeature<ClusterFeatureEntity>) => {
        openPopup({
            is_change_date: false,
            is_add_to_wait_list: false,
            is_assign: false,
            is_self_delivery: true,
            feature
        })
    }

    const initializeMapClusterer = async () => {
        if (!mapInstance) return

        ordersClusterer = await mapCluster.createCluster<ClusterFeatureEntity>({
            gridSize: GRID_SIZE,
            features: getActualClusterFeatures().map(dwMapHelpers.convertOrderToClusterFeature),
            markerTemplate: dwMapHelpers.convertFeatureToMarkerElement,
            clusterTemplate: dwMapHelpers.convertFeaturesToClusterElement,
            clusterPopup: (
                coordinates: LngLat,
                features: Array<ClusterFeatureEntity>,
                close: () => void,
            ) => dwMapHelpers.createClusterPopup(
                coordinates,
                features,
                selectedFeatureId.value,
                close,
                {
                    changeDate: onFeatureChangeDate,
                    addToWaitList: onFeatureAddToWaitList,
                    assign: onFeatureAssign,
                    selfDelivery: onFeatureSelfDelivery
                }
            ),
            markerPopup: (
                feature: ClusterFeatureEntity,
                close: () => void
            ) => dwMapHelpers.createMarkerPopup(
                feature,
                close,
                {
                    changeDate: onFeatureChangeDate,
                    addToWaitList: onFeatureAddToWaitList,
                    assign: onFeatureAssign,
                    selfDelivery: onFeatureSelfDelivery
                }
            )
        })

        mapInstance.addChild(ordersClusterer)
    }

    const destroyMapClusterer = () => {
        if (!mapInstance || !ordersClusterer) return

        mapInstance.removeChild(ordersClusterer)
        ordersClusterer = null
    }

    const initializeModule = (options: DispatcherWorkplaceInitializeHelperModuleOption & { forCouriers: boolean }) => {
        mapInstance = options.mapInstance
        ordersControlsGroup = options.controlsGroup

        if (options.forCouriers) {
            state.quick_filters.completed_orders = false
            state.quick_filters.deliveries = false
            state.quick_filters.takes = false

            initializeMapClusterer()
        } else {
            initializeMapControls()
            initializeMapClusterer()
        }
    }

    const destroyModule = (options: DispatcherWorkplaceDestroyHelperModuleOption) => {
        destroyMapControls()
        destroyMapClusterer()

        if (options.resetStatement) {
            state.quick_filters.completed_orders = false
            state.quick_filters.undelivered_deliveries = false
            state.quick_filters.unassigned_takes = false
            state.quick_filters.order_or_invoice_number = null
        }
    }

    const openClusterPopupOnMap = (dataEntity: DispatcherWorkplaceDataEntity) => {
        if (ordersClusterer) {
            if (dataEntity.type === 1) {
                ordersClusterer.openPopup(`order-${(dataEntity as DispatcherWorkplaceTake).orderNumber}`)
            }

            if (dataEntity.type === 2) {
                ordersClusterer.openPopup(`invoice-${(dataEntity as DispatcherWorkplaceDelivery).invoiceNumber}`)
            }
        }
    }

    const onEntityClicked = (dataEntity: DispatcherWorkplaceDataEntity) => {
        dwMapHelpers.updateMapLocation({
            map: mapInstance as YMap,
            coordinates: [Number(dataEntity.longitude as string), Number(dataEntity.latitude as string)],
            zoom: 17,
        })

        if (dataEntity.type === 1) {
            if ((dataEntity as DispatcherWorkplaceTake).notAssigned && !state.quick_filters.unassigned_takes) {
                state.quick_filters.unassigned_takes = true
                updateCluster()
            }
        }

        if (dataEntity.type === 2) {
            if ((dataEntity as DispatcherWorkplaceDelivery).notDelivered && !state.quick_filters.undelivered_deliveries) {
                state.quick_filters.undelivered_deliveries = true
                updateCluster()
            }
        }

        openClusterPopupOnMap(dataEntity)
    }

    const setQuickFiltersCallbacks = (couriersQuickFiltersCallbacks: QuickFiltersCallbacks) => {
        if (couriersQuickFiltersCallbacks) {
            quickFiltersCallbacks = {
                onUndeliveredDeliveriesChanged: couriersQuickFiltersCallbacks.onUndeliveredDeliveriesChanged,
                onUnassignedTakesChanged: couriersQuickFiltersCallbacks.onUnassignedTakesChanged
            }
        }
    }

    const setUndeliveredDeliveriesQuckFilters = (is_enabled: boolean) => {
        state.quick_filters.undelivered_deliveries = is_enabled

        if (is_enabled) {
            state.quick_filters.completed_orders = false
        }

        updateCluster()
    }

    const setUnassignedTakesQuckFilters = (is_enabled: boolean) => {
        state.quick_filters.unassigned_takes = is_enabled

        if (is_enabled) {
            state.quick_filters.completed_orders = false
        }

        updateCluster()
    }

    return {
        state,
        updateList,
        initializeModule,
        destroyModule,
        onEntityClicked,
        setQuickFiltersCallbacks,
        setUndeliveredDeliveriesQuckFilters,
        setUnassignedTakesQuckFilters,
    }
}